import React from "react";
import {
  CustomTicketV2,
  Event,
  EventType,
  MediaAspectRatio,
} from "@markit/common.types";
import { MEDIA_UPLOAD_CODES, MediaUpload } from "./photoUtils";
import {
  firebaseStorage,
  getDownloadURL,
  ref,
  uploadBytesResumable,
  deleteObject,
} from "../firebase";
import { UploadTask } from "firebase/storage";
import { getEventData } from "./FirebaseUtils";
import {
  getCanonicalMediaUrls,
  getEventBaseStoragePath,
  getProductBaseStoragePath,
  isFirebaseStorageUrl,
  isProductSpecificType,
} from "@markit/common.utils";

export const MAX_MEDIA_PRODUCT_SIZE = 500000000; // 500MB max product size for media
export const MAX_MULTI_IMAGES = 12; // The max number of images that can be uploaded for an event/product

// Video upload constants - larger file size limits for videos
export const VALID_VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "audio/mpeg",
  "audio/mp3",
];
export const VALID_VIDEO_EXTENSIONS = [".mp4", ".mov", ".avi", ".mp3"];

// Audio upload constants
export const VALID_AUDIO_TYPES = [
  "audio/mpeg",
  "audio/mp3",
  "audio/m4a",
  "audio/aac",
  "audio/wav",
  "audio/x-m4a",
];
export const VALID_AUDIO_EXTENSIONS = [".mp3", ".m4a", ".aac", ".wav"];

// Shows the supported file types for a given event type
export const supportedFileTypes = (eventType: EventType) => {
  return eventType === EventType.VIDEO
    ? ["MP3", "MP4", "MOV", "AVI"]
    : eventType === EventType.AUDIO
      ? ["MP3", "M4A", "AAC", "WAV"]
      : eventType === EventType.CUSTOM_PRODUCT
        ? ["PNG", "JPG", "JPEG"]
        : ["PDF"];
};

export const errorMediaAlertText = (code: string) => {
  switch (code) {
    case MEDIA_UPLOAD_CODES.TOO_BIG:
      return `Media is too big (max. ${MAX_MEDIA_PRODUCT_SIZE / 1000000}MB)`;
    case MEDIA_UPLOAD_CODES.INVALID:
      return "Invalid Media Type";
    case MEDIA_UPLOAD_CODES.PERMISSIONS:
      return "Sorry, please go into settings and enable camera permissions";
    default:
      return "";
  }
};

export const errorMediaSubAlertText = (code: string, fileTypes: string[]) => {
  switch (code) {
    case MEDIA_UPLOAD_CODES.TOO_BIG:
      return "Try again with a smaller media file";
    case MEDIA_UPLOAD_CODES.INVALID:
      return `Try again with a valid media type.\nValid media types include: ${fileTypes.join(", ")}`;
    case MEDIA_UPLOAD_CODES.PERMISSIONS:
      return "";
    default:
      return "";
  }
};

// Validates if a file is a valid media type
const isValidMediaType = (file: File, type: EventType): boolean => {
  // Check MIME type
  if (type === EventType.VIDEO && VALID_VIDEO_TYPES.includes(file.type)) {
    return true;
  } else if (
    type === EventType.AUDIO &&
    VALID_AUDIO_TYPES.includes(file.type)
  ) {
    return true;
  } else if (type === EventType.PDF && file.type === "application/pdf") {
    return true;
  }

  // Check file extension as fallback
  const fileName = file.name.toLowerCase();
  return VALID_VIDEO_EXTENSIONS.some((ext) => fileName.endsWith(ext));
};

// Uploads a video/audio file without compression (media are typically too large to compress client-side)
// Returns a blob URL for preview and an upload code
export const uploadVideo = async (
  input: React.ChangeEvent<HTMLInputElement> | File,
  type: EventType
): Promise<MediaUpload> => {
  try {
    const myFile = input instanceof File ? input : input.target.files?.[0];

    if (!myFile) {
      return {
        blobUrl: "",
        uploadCode: MEDIA_UPLOAD_CODES.INVALID,
      };
    }

    // Validate file type
    if (!isValidMediaType(myFile, type)) {
      return {
        blobUrl: "",
        uploadCode: MEDIA_UPLOAD_CODES.INVALID,
      };
    }

    // Check file size
    if (myFile.size > MAX_MEDIA_PRODUCT_SIZE) {
      return {
        blobUrl: "",
        uploadCode: MEDIA_UPLOAD_CODES.TOO_BIG,
      };
    }

    // Create blob URL for preview (no compression for videos)
    const blobUrl = URL.createObjectURL(myFile);

    return {
      blobUrl,
      uploadCode: MEDIA_UPLOAD_CODES.SUCCESS,
    };
  } catch (e: any) {
    console.error("Error uploading video:", e);
    return {
      blobUrl: "",
      uploadCode: MEDIA_UPLOAD_CODES.INVALID,
    };
  }
};

// Saves a product file to a specified pathString in firebase storage
// Handles larger file sizes and proper content types
export const saveProductMediaToFirestore = async (
  url: string,
  pathString: string,
  setProgress: (progress: number) => void,
  onUploadTask: (uploadTask: UploadTask) => void
): Promise<string> => {
  try {
    // If the URL is already a Firebase Storage URL, return it
    if (isFirebaseStorageUrl(url)) {
      return url;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch video: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    const videoRef = ref(firebaseStorage, pathString);

    // Determine content type from blob or parameter
    const metadata = { contentType: blob.type || "video/mp4" };

    const uploadTask = uploadBytesResumable(videoRef, blob, metadata);

    // Expose the upload task to the caller for cancellation
    if (onUploadTask) {
      onUploadTask(uploadTask);
    }

    return new Promise<string>((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          // Only set progress if total bytes is greater than 10MB
          if (snapshot.totalBytes && snapshot.totalBytes > 10000000) {
            setProgress(Math.trunc(progress));
          }
        },
        (error) => {
          console.error("Upload error:", error);
          if (error.code === "storage/canceled") {
            resolve("");
          } else {
            reject(error);
          }
        },
        async () => {
          try {
            const downloadUrl = await getDownloadURL(videoRef);
            if (downloadUrl.startsWith("blob:")) {
              throw new Error(
                "Firebase returned a blob URL instead of a proper download URL"
              );
            }

            resolve(downloadUrl);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  } catch (error: any) {
    console.error("Error in saveProductMediaToFirestore:", error.message);
    throw error;
  }
};

// Gets the aspect ratio of a video from its URL
// Returns the closest matching MediaAspectRatio enum value
export const getVideoAspectRatio = async (
  videoUrl: string
): Promise<MediaAspectRatio | null> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      try {
        const width = video.videoWidth;
        const height = video.videoHeight;

        if (!width || !height) {
          resolve(null);
          return;
        }

        const aspectRatio = width / height;

        // Define the aspect ratios and their corresponding enum values
        const ratios = [
          { value: 16 / 9, enum: MediaAspectRatio._16_9 },
          { value: 4 / 3, enum: MediaAspectRatio._4_3 },
          { value: 9 / 16, enum: MediaAspectRatio._9_16 },
          { value: 3 / 4, enum: MediaAspectRatio._3_4 },
          { value: 1 / 1, enum: MediaAspectRatio._1_1 },
        ];

        // Find the closest matching aspect ratio
        let closestRatio = ratios[0];
        let minDifference = Math.abs(aspectRatio - ratios[0].value);

        for (const ratio of ratios) {
          const difference = Math.abs(aspectRatio - ratio.value);
          if (difference < minDifference) {
            minDifference = difference;
            closestRatio = ratio;
          }
        }

        resolve(closestRatio.enum);
      } catch (error) {
        console.error("Error calculating aspect ratio:", error);
        resolve(null);
      }
    };

    video.onerror = (error) => {
      console.error("Error loading video for aspect ratio detection:", error);
      resolve(null);
    };
    video.src = videoUrl;
  });
};

// Generates a thumbnail image from a video URL
// Returns a blob URL of the thumbnail image
export const generateVideoThumbnail = async (
  videoUrl: string,
  seekTime: number = 0
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      // Set the video time to the desired seek time (default the start of the video)
      video.currentTime = Math.min(seekTime, video.duration || 0);
    };

    video.onseeked = () => {
      try {
        // Create a canvas to draw the video frame
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        // Draw the video frame to the canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob URL
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to create thumbnail blob"));
              return;
            }
            const thumbnailUrl = URL.createObjectURL(blob);
            resolve(thumbnailUrl);
          },
          "image/jpeg",
          0.9
        );
      } catch (error) {
        reject(error);
      }
    };

    video.onerror = (error) => {
      console.error("Error loading video for thumbnail generation:", error);
      reject(error);
    };

    // Set the video source and start loading
    video.src = videoUrl;
  });
};

// Helper function to get file extension from MIME type
const getExtensionFromMimeType = (mimeType: string): string => {
  const mimeToExt: { [key: string]: string } = {
    "video/mp4": ".mp4",
    "video/quicktime": ".mov",
    "video/x-msvideo": ".avi",
    "video/webm": ".webm",
    "audio/mpeg": ".mp3",
    "audio/mp3": ".mp3",
    "audio/m4a": ".m4a",
    "audio/x-m4a": ".m4a",
    "audio/aac": ".aac",
    "audio/wav": ".wav",
    "audio/webm": ".weba",
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/jpg": ".jpg",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "application/pdf": ".pdf",
  };
  return mimeToExt[mimeType] || "";
};

const removeFileExtensionFromTitle = (title: string): string => {
  return title.replace(/\.[A-Za-z][A-Za-z0-9]{0,9}$/, "");
};

// Downloads specified media files from firebase storage with the same file type as stored
export const downloadMediaFromFirestore = async (
  mediaUrls: string[],
  title: string
) => {
  try {
    const titleWithoutExt = removeFileExtensionFromTitle(title);

    for (let i = 0; i < mediaUrls.length; i++) {
      const downloadUrl = mediaUrls[i];
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error(
          `Failed to download media: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      const ext = getExtensionFromMimeType(blob.type);
      const downloadFileName =
        mediaUrls.length > 1
          ? `${titleWithoutExt}-${i + 1}${ext}`
          : `${titleWithoutExt}${ext}`;

      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    }
  } catch (error: any) {
    console.error("Error downloading media from firestore:", error);
    throw error;
  }
};

/**
 * Builds final media URL list: keeps Firebase URLs, uploads others via uploadOne.
 * Throws on upload failure to avoid silently clearing images when save would overwrite with empty mediaURLs.
 */
export const buildFinalMediaUrls = async (
  mediaUrls: string[],
  uploadOne: (url: string, index: number) => Promise<string | null>
): Promise<string[]> => {
  const cleaned = mediaUrls.filter(Boolean);
  const finalUrls: string[] = [];
  for (let i = 0; i < cleaned.length; i++) {
    const url = cleaned[i];
    if (isFirebaseStorageUrl(url)) {
      finalUrls.push(url);
      continue;
    }
    try {
      const uploaded = await uploadOne(url, i);
      if (!uploaded) {
        throw new Error("Failed to upload media");
      }
      finalUrls.push(uploaded);
    } catch (err: any) {
      console.error("Error uploading media:", err?.message);
      throw err;
    }
  }
  return finalUrls;
};

/** Deletes from Storage any previous media URLs that are owned, Firebase, and not in finalMediaUrls. */
export const deleteRemovedMediaFromStorage = async (
  event: Event,
  finalMediaUrls: string[],
  isOwnedUrl: (url: string) => boolean
): Promise<void> => {
  const previousEvent = await getEventData(event.id);
  const previousUrls = previousEvent
    ? getCanonicalMediaUrls(previousEvent)
    : [];
  const removed = previousUrls.filter(
    (url) =>
      isFirebaseStorageUrl(url) &&
      isOwnedUrl(url) &&
      !finalMediaUrls.includes(url)
  );
  await Promise.all(
    removed.map(async (url) => {
      try {
        await deleteObject(ref(firebaseStorage, url));
      } catch (err: any) {
        if (err?.code !== "storage/object-not-found") {
          console.log("Error deleting removed media:", err.message);
        }
      }
    })
  );
};

/**
 * Deletes stored custom ticket images when a ticket is removed, or when its image
 * is cleared or replaced. Uses persisted event data (same pattern as deleteRemovedMediaFromStorage).
 */
export const deleteRemovedCustomTicketMediaFromStorage = async (
  event: Event,
  finalCustomTickets: CustomTicketV2[]
): Promise<void> => {
  const previousEvent = await getEventData(event.id);
  if (!previousEvent) return;

  const basePath = isProductSpecificType(event.eventType)
    ? getProductBaseStoragePath(event.createdBy, event.id)
    : getEventBaseStoragePath(event.createdBy, event.id);

  // Checks it the mediaUrl is a Firebase Storage URL and includes the base creator storage path
  const isOwnedTicketMediaUrl = (url: string) => {
    try {
      return decodeURIComponent(url).includes(basePath);
    } catch {
      return url.includes(basePath);
    }
  };

  const deleteFirebaseUrl = async (url: string) => {
    if (!url || !isFirebaseStorageUrl(url) || !isOwnedTicketMediaUrl(url)) {
      return;
    }
    try {
      await deleteObject(ref(firebaseStorage, url));
    } catch (err: any) {
      if (err?.code !== "storage/object-not-found") {
        console.log("Error deleting custom ticket media:", err.message);
      }
    }
  };

  const finalById = new Map(finalCustomTickets.map((t) => [t.id, t]));

  for (const prev of previousEvent.customTickets) {
    const next = finalById.get(prev.id);

    // If the custom ticket is not in the final custom tickets, delete the media URL
    if (!next) {
      await deleteFirebaseUrl(prev.mediaURL ?? "");
      continue;
    }

    // If the custom ticket still exists, check if the media URL has changed.
    // We only delete previous media when the next URL is no longer an owned
    // Firebase URL (cleared, changed to blob/external URL, etc).
    // For normal replacement uploads on the same ticket id, we keep the same
    // storage object path and only tokenized download URL changes, so skip delete.
    const prevUrl = prev.mediaURL || "";
    const nextUrl = next.mediaURL || "";
    if (
      prevUrl &&
      isFirebaseStorageUrl(prevUrl) &&
      isOwnedTicketMediaUrl(prevUrl) &&
      prevUrl !== nextUrl
    ) {
      const nextKeepsOwnedFirebaseMedia =
        !!nextUrl &&
        isFirebaseStorageUrl(nextUrl) &&
        isOwnedTicketMediaUrl(nextUrl);

      if (!nextKeepsOwnedFirebaseMedia) {
        await deleteFirebaseUrl(prevUrl);
      }
    }
  }
};

// Checks if a given aspect ratio is a portrait aspect ratio
export const isPortraitAspectRatio = (aspectRatio: MediaAspectRatio) => {
  return (
    aspectRatio === MediaAspectRatio._9_16 ||
    aspectRatio === MediaAspectRatio._3_4 ||
    aspectRatio === MediaAspectRatio._4_5
  );
};
